/* eslint-disable no-console */
/**
 * Gestion d'une connexion internet mauvaise ou coupée.
 *
 * Deux rôles :
 *  - `verifierSessionAvecReprise` : au démarrage, la vérification de session
 *    (`chektoken`) qui n'obtient AUCUNE réponse du serveur ne déconnecte plus
 *    l'utilisateur. On garde le token, on affiche un écran d'attente et on
 *    réessaie jusqu'au retour du réseau. Seule une réponse explicite du serveur
 *    (session refusée) renvoie à la page de connexion.
 *  - `demarrerSurveillanceReseau` : bandeau d'avertissement non bloquant quand
 *    le poste est hors connexion ou qu'une requête n'aboutit pas.
 *
 * Volontairement en DOM natif et styles en ligne, comme appUpdateGate : l'écran
 * d'attente doit pouvoir s'afficher avant le montage de l'application.
 */

// `require` et non `import` : c'est l'instance axios utilisée par le store et
// par les composants. Les intercepteurs posés ici voient donc leurs requêtes.
const axios = require('axios');

const ID_BANDEAU = 'hsig-reseau-bandeau';
const ID_ATTENTE = 'hsig-reseau-attente';

/** Délais entre deux tentatives de vérification de session (ms). */
const DELAIS_REPRISE = [3000, 5000, 10000];

/** Le bandeau « instable » s'efface seul si plus rien n'échoue pendant ce délai. */
const DUREE_BANDEAU_INSTABLE_MS = 20000;

/**
 * Vrai si la requête n'a pas atteint l'application : pas de réponse du tout
 * (coupure, délai dépassé) ou réponse d'une passerelle (502/503/504).
 * Une requête annulée volontairement n'est pas une panne.
 */
function estErreurReseau(error) {
    if (!error) return false;
    if (error.code === 'ERR_CANCELED') return false;
    if (!error.response) return true;
    return [502, 503, 504].includes(error.response.status);
}

// ───────────────────────────── Bandeau ─────────────────────────────

let minuterieBandeau = null;

function afficherBandeau(texte) {
    try {
        let bandeau = document.getElementById(ID_BANDEAU);
        if (!bandeau) {
            bandeau = document.createElement('div');
            bandeau.id = ID_BANDEAU;
            bandeau.setAttribute('role', 'status');
            bandeau.style.cssText = [
                'position:fixed', 'top:8px', 'left:50%', 'transform:translateX(-50%)',
                // Au-dessus des modales de l'application, sous la modale de mise à jour.
                'z-index:2147483000',
                'max-width:calc(100vw - 24px)',
                'padding:8px 16px', 'border-radius:999px',
                'background:#B91C1C', 'color:#fff',
                'font:600 13px/1.4 system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif',
                'box-shadow:0 6px 20px rgba(0,0,0,.25)',
                'text-align:center',
                // Ne doit jamais empêcher de cliquer sur la barre du haut.
                'pointer-events:none',
            ].join(';');
            document.body.appendChild(bandeau);
        }
        bandeau.textContent = texte;
    } catch (e) { /* DOM indisponible */ }
}

function masquerBandeau() {
    clearTimeout(minuterieBandeau);
    try {
        const bandeau = document.getElementById(ID_BANDEAU);
        if (bandeau) bandeau.remove();
    } catch (e) { /* DOM indisponible */ }
}

function signalerHorsConnexion() {
    clearTimeout(minuterieBandeau);
    afficherBandeau('Hors connexion vérifiez votre accès internet');
}

function signalerInstable() {
    if (navigator.onLine === false) return signalerHorsConnexion();
    afficherBandeau('Connexion instable la dernière opération n\'a pas abouti, réessayez');
    clearTimeout(minuterieBandeau);
    minuterieBandeau = setTimeout(masquerBandeau, DUREE_BANDEAU_INSTABLE_MS);
}

let surveillanceDemarree = false;

/** Idempotent. À appeler une fois au démarrage. */
function demarrerSurveillanceReseau() {
    if (surveillanceDemarree) return;
    surveillanceDemarree = true;

    window.addEventListener('offline', signalerHorsConnexion);
    window.addEventListener('online', masquerBandeau);
    if (navigator.onLine === false) signalerHorsConnexion();

    // Intercepteur transparent : il observe, il ne modifie ni la réponse ni l'erreur.
    axios.interceptors.response.use(
        function (response) {
            if (navigator.onLine !== false) masquerBandeau();
            return response;
        },
        function (error) {
            if (estErreurReseau(error)) signalerInstable();
            return Promise.reject(error);
        }
    );
}

// ───────────────────────── Écran d'attente ─────────────────────────

function afficherAttente(tentative, surReessayer) {
    try {
        let racine = document.getElementById(ID_ATTENTE);
        if (!racine) {
            racine = document.createElement('div');
            racine.id = ID_ATTENTE;
            racine.setAttribute('role', 'alertdialog');
            racine.setAttribute('aria-modal', 'true');
            racine.style.cssText = [
                'position:fixed', 'inset:0', 'top:0', 'right:0', 'bottom:0', 'left:0',
                'background:#F8FAFC',
                'z-index:2147483600',
                'display:flex', 'align-items:center', 'justify-content:center',
                'padding:16px',
                'font-family:system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif',
            ].join(';');
            racine.innerHTML = `
              <div style="background:#fff;border-radius:14px;max-width:420px;width:100%;
                          padding:28px 26px 24px;text-align:center;
                          box-shadow:0 12px 40px rgba(15,23,42,.12);">
                <div style="width:60px;height:60px;margin:0 auto 18px;border-radius:50%;
                            background:#FEE2E2;display:flex;align-items:center;
                            justify-content:center;">
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none"
                       stroke="#B91C1C" stroke-width="2" stroke-linecap="round"
                       stroke-linejoin="round" aria-hidden="true">
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
                    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
                    <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
                    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
                    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
                    <line x1="12" y1="20" x2="12.01" y2="20"></line>
                  </svg>
                </div>
                <h2 style="margin:0 0 10px;font-size:19px;font-weight:600;color:#0F172A;">
                  Connexion instable
                </h2>
                <p style="margin:0 0 8px;font-size:14px;line-height:1.55;color:#475569;">
                  Le serveur ne répond pas. Votre session est conservée :
                  l'application reprendra dès le retour de la connexion.
                </p>
                <p id="${ID_ATTENTE}-info" style="margin:0 0 20px;font-size:13px;color:#64748B;"></p>
                <button id="${ID_ATTENTE}-btn" type="button"
                        style="border:none;border-radius:8px;padding:10px 22px;
                               background:#0F172A;color:#fff;font-size:14px;
                               font-weight:600;cursor:pointer;">
                  Réessayer maintenant
                </button>
              </div>`;
            document.body.appendChild(racine);
        }
        const info = document.getElementById(ID_ATTENTE + '-info');
        if (info) info.textContent = `Nouvelle tentative automatique… (essai n° ${tentative})`;
        const bouton = document.getElementById(ID_ATTENTE + '-btn');
        if (bouton) bouton.onclick = surReessayer;
    } catch (e) { /* DOM indisponible */ }
}

function masquerAttente() {
    try {
        const racine = document.getElementById(ID_ATTENTE);
        if (racine) racine.remove();
    } catch (e) { /* DOM indisponible */ }
}

/**
 * Vérifie la session auprès du serveur (action `chektoken` du store).
 *
 * @param {object}  store
 * @param {string}  idtoken
 * @param {boolean} reprise  true : sur panne réseau, afficher l'écran d'attente
 *                           et réessayer jusqu'à obtenir une réponse.
 *                           false : rejeter tout de suite (routes publiques).
 * @returns {Promise} résolue avec la réponse de `chektoken` ; rejetée sur une
 *                    erreur qui n'est pas une panne réseau.
 */
function verifierSessionAvecReprise(store, idtoken, reprise = true) {
    return new Promise((resolve, reject) => {
        let tentative = 0;
        let minuterie = null;
        let enCours = false;

        const terminer = () => {
            clearTimeout(minuterie);
            window.removeEventListener('online', essayer);
            masquerAttente();
        };

        function essayer() {
            if (enCours) return;
            enCours = true;
            clearTimeout(minuterie);

            store.dispatch('chektoken', { idtoken })
                .then((res) => {
                    enCours = false;
                    terminer();
                    resolve(res);
                })
                .catch((error) => {
                    enCours = false;
                    if (!reprise || !estErreurReseau(error)) {
                        terminer();
                        return reject(error);
                    }
                    tentative++;
                    console.warn(`[RESEAU] Vérification de session sans réponse (essai ${tentative})`);
                    afficherAttente(tentative, essayer);
                    const delai = DELAIS_REPRISE[Math.min(tentative - 1, DELAIS_REPRISE.length - 1)];
                    minuterie = setTimeout(essayer, delai);
                });
        }

        // Le retour du réseau déclenche un essai immédiat, sans attendre la minuterie.
        window.addEventListener('online', essayer);
        essayer();
    });
}

export { estErreurReseau, demarrerSurveillanceReseau, verifierSessionAvecReprise };
